/**
 * Encoge la foto antes de subirla.
 *
 * El problema que resuelve: un teléfono reciente saca fotos de ocho a
 * doce megas. El técnico está en una tienda, con la señal que hay
 * dentro de una bodega, y sube tres de ésas al cerrar la orden. La
 * subida tarda, a veces se corta, y una evidencia que falló no la
 * reintenta nadie: la orden se cierra sin ella.
 *
 * Lo que se gana no es espacio en disco —eso es barato— sino que la
 * subida termine.
 *
 * ── Por qué en el navegador y no en el servidor ──────────────────────
 *
 * Porque el cuello de botella es la subida, no el guardado. Comprimir
 * del lado del servidor ahorraría disco y no ahorraría ni un segundo de
 * la espera del técnico, que es el problema.
 */

/**
 * El lado largo máximo, en píxeles.
 *
 * Mil seiscientos alcanza de sobra para lo que estas fotos tienen que
 * probar: que la pieza estaba rota, que el equipo quedó armado, que la
 * orden de servicio trae firma. A tamaño completo en pantalla se lee
 * perfectamente y en papel también.
 */
const LADO_MAXIMO = 1600;

/**
 * La calidad del JPEG.
 *
 * 0.82 es el punto donde una foto de taller deja de distinguirse del
 * original a simple vista. Más arriba, el archivo crece sin que nadie
 * note la diferencia; más abajo empiezan a verse bloques en las zonas
 * lisas, que es justo donde está el número de serie grabado.
 */
const CALIDAD = 0.82;

/**
 * Se comprime a partir de aquí, y no siempre.
 *
 * Una foto que ya pesa poco no gana nada pasando por el canvas, y sí
 * pierde: se reencoda, se le va el EXIF y queda peor que el original.
 */
const DESDE_BYTES = 1.5 * 1024 * 1024;

/**
 * Devuelve la foto lista para subir: encogida si valía la pena,
 * y la original si no.
 *
 * Nunca falla hacia afuera. Si el navegador no puede con el canvas —una
 * imagen corrupta, un formato que no decodifica, memoria— devuelve el
 * archivo tal cual: que la foto pese es un problema, que no se suba es
 * uno peor.
 */
export async function aligerar(archivo: File): Promise<File> {
  // El PDF y lo que no sea imagen no se tocan: no hay nada que escalar
  // y pasarlo por el canvas lo destruiría.
  if (!archivo.type.startsWith('image/')) return archivo;
  if (archivo.size <= DESDE_BYTES) return archivo;

  try {
    const mapa = await leer(archivo);

    const escala = Math.min(1, LADO_MAXIMO / Math.max(mapa.width, mapa.height));
    const ancho = Math.round(mapa.width * escala);
    const alto = Math.round(mapa.height * escala);

    const lienzo = document.createElement('canvas');
    lienzo.width = ancho;
    lienzo.height = alto;

    const pincel = lienzo.getContext('2d');
    if (!pincel) return archivo;

    pincel.drawImage(mapa, 0, 0, ancho, alto);
    cerrar(mapa);

    const trozo = await new Promise<Blob | null>((resolver) => {
      lienzo.toBlob(resolver, 'image/jpeg', CALIDAD);
    });

    // Si por lo que sea salió más grande que el original, se manda el
    // original. Pasa con capturas de pantalla y con imágenes ya muy
    // comprimidas.
    if (!trozo || trozo.size >= archivo.size) return archivo;

    return new File([trozo], nombreJpeg(archivo.name), {
      type: 'image/jpeg',
      lastModified: archivo.lastModified,
    });
  } catch {
    return archivo;
  }
}

/**
 * Decodifica la imagen, con `createImageBitmap` si lo hay.
 *
 * Es el camino rápido y el que no bloquea la pantalla mientras
 * decodifica, que con una foto de doce megas se nota. El `<img>` es el
 * respaldo para los navegadores que no lo traen.
 */
async function leer(archivo: File): Promise<ImageBitmap | HTMLImageElement> {
  if (typeof createImageBitmap === 'function') {
    return createImageBitmap(archivo);
  }

  const url = URL.createObjectURL(archivo);
  try {
    return await new Promise<HTMLImageElement>((resolver, rechazar) => {
      const img = new Image();
      img.onload = () => resolver(img);
      img.onerror = () => rechazar(new Error('no se pudo leer la imagen'));
      img.src = url;
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** Libera el mapa de bits. Sin esto, veinte fotos seguidas tiran la pestaña. */
function cerrar(mapa: ImageBitmap | HTMLImageElement): void {
  if ('close' in mapa) mapa.close();
}

/**
 * El nombre, con extensión .jpg.
 *
 * Importa porque el nombre es lo que se ve en la lista de evidencias, y
 * un archivo que dice `.png` y por dentro es JPEG confunde a quien lo
 * descarga.
 */
function nombreJpeg(nombre: string): string {
  return `${nombre.replace(/\.[^.]+$/, '')}.jpg`;
}
